import { Button, type ButtonProps } from '@heroui/react';
import type { ReactNode } from 'react';

/**
 * Buttons with explicit action priorities.
 *
 * A page should make it obvious which action is THE action. Rather than each
 * page choosing a HeroUI `color`/`variant` pair by eye, priority is named and
 * the mapping lives here — so "primary" looks the same on every screen, and
 * changing what primary looks like is one edit.
 *
 *  primary     the one thing this page is for. At most one per view.
 *  secondary   a real alternative, weighted below primary.
 *  tertiary    a low-emphasis action: cancel, back, an inline link-like verb.
 *  destructive one that removes, deactivates or cancels something. Always
 *              paired with a confirmation dialog — see ConfirmDialog.
 *
 * `isLoading` sets HeroUI's spinner AND disables the control, so a slow save
 * cannot be submitted twice. It is driven by the request's real pending state,
 * never by an optimistic flag (CLAUDE.md section 8).
 */

export type ActionPriority =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'destructive';

const PRIORITY_PROPS: Record<
  ActionPriority,
  Pick<ButtonProps, 'color' | 'variant'>
> = {
  primary: { color: 'primary', variant: 'solid' },
  secondary: { color: 'default', variant: 'bordered' },
  tertiary: { color: 'default', variant: 'light' },
  destructive: { color: 'danger', variant: 'solid' },
};

/**
 * Text colour a priority needs beyond its HeroUI pair.
 *
 * The neutral scale's `foreground` is WHITE, because it is the ink for a solid
 * neutral fill — and HeroUI's `light` variant reuses that same foreground as
 * its text. Every tertiary action therefore rendered white on the light
 * ground: "Back", "Cancel" and the like measured 1.09:1 in a real browser
 * during the Phase 8 check, where WCAG AA asks 4.5:1. Tertiary takes the page
 * ink instead. A caller's own `className` is applied after this, so a tertiary
 * button placed on the navy rail keeps the rail's ink.
 */
const PRIORITY_CLASS: Partial<Record<ActionPriority, string>> = {
  tertiary: 'text-foreground',
};

export interface ActionButtonProps
  extends Omit<ButtonProps, 'color' | 'variant' | 'children'> {
  readonly priority?: ActionPriority;
  /** Always translated text from the caller; never a literal. */
  readonly children: ReactNode;
  readonly isLoading?: boolean;
}

export function ActionButton({
  priority = 'secondary',
  isLoading = false,
  children,
  className,
  ...rest
}: ActionButtonProps) {
  const classes = [PRIORITY_CLASS[priority], className].filter(Boolean).join(' ');
  return (
    <Button
      {...PRIORITY_PROPS[priority]}
      {...rest}
      className={classes || undefined}
      isLoading={isLoading}
      isDisabled={rest.isDisabled || isLoading}
    >
      {children}
    </Button>
  );
}

/**
 * The action row at the foot of a form or dialog.
 *
 * `justify-end` resolves to the trailing edge, which mirrors under RTL, and
 * the primary action is placed LAST in the DOM so tab order reaches the
 * confirming action after the cancelling one in both directions.
 */
export function ActionBar({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-inline">
      {children}
    </div>
  );
}
